const I18N = {
    vi: {
        today: 'Hôm nay',
        yesterday: 'Hôm qua',
        weekdays: {
            0: 'CN',
            1: 'T2',
            2: 'T3',
            3: 'T4',
            4: 'T5',
            5: 'T6',
            6: 'T7'
        }
    },
    en: {
        today: 'Today',
        yesterday: 'Yesterday'
    }
};

function pad(n) {
    return String(n).padStart(2, '0');
}

/* ========================================================
   ✅ DATE LABEL — dùng cho TIMELINE
======================================================== */
export function formatDateLabel(time, locale = 'vi') {
    if (!time) return '';

    const d = new Date(time);
    if (isNaN(d.getTime())) return '';

    const now = new Date();
    const i18n = I18N[locale] ?? I18N.vi;

    const sameYear = d.getFullYear() === now.getFullYear();

    const key = (x) =>
        `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;

    const todayKey = key(now);
    const yesterdayKey = key(
        new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
    );

    const dateKey = key(d);

    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();

    const weekday =
        locale === 'vi'
            ? i18n.weekdays[d.getDay()]
            : d.toLocaleDateString(locale, { weekday: 'short' });

    if (dateKey === todayKey) {
        return `${i18n.today}, ${day}/${month}`;
    }

    if (dateKey === yesterdayKey) {
        return `${i18n.yesterday}, ${weekday}, ${day}/${month}`;
    }

    if (!sameYear) {
        return `${weekday}, ${day}/${month}/${year}`;
    }

    return `${weekday}, ${day}/${month}`;
}

/* ========================================================
   ✅ TIME LABEL — dùng trong MESSAGE BUBBLE
======================================================== */
export function formatTimeLabel(time, locale = 'vi') {
    if (!time) return '';
    //console.log('time:', time);
    const d = new Date(time);
    if (isNaN(d.getTime())) return '';

    const now = new Date();
    const i18n = I18N[locale] ?? I18N.vi;

    const key = (x) =>
        `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;

    const todayKey = key(now);
    const yesterdayKey = key(
        new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
    );
    const dateKey = key(d);

    const hour = pad(d.getHours());
    const minute = pad(d.getMinutes());
    const timeStr = `${hour}:${minute}`;

    // ✅ HÔM NAY → chỉ giờ
    if (dateKey === todayKey) {
        return timeStr;
    }

    // ✅ HÔM QUA → Hôm qua + giờ
    if (dateKey === yesterdayKey) {
        return `${i18n.yesterday}, ${timeStr}`;
    }

    // ✅ NGÀY KHÁC → chỉ giờ
    return timeStr;
}

/* ========================================================
   ✅ HELPER — so sánh ngày
======================================================== */
export function isDifferentDay(a, b) {
    if (!a || !b) return true;

    const d1 = new Date(a);
    const d2 = new Date(b);

    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return true;

    return (
        d1.getFullYear() !== d2.getFullYear() ||
        d1.getMonth() !== d2.getMonth() ||
        d1.getDate() !== d2.getDate()
    );
}

export function formatDuration(seconds = 0) {
    seconds = Math.floor(seconds);

    if (seconds < 60) {
        return `${seconds} giây`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainSeconds = seconds % 60;

    if (minutes < 60) {
        return remainSeconds > 0
            ? `${minutes} phút ${remainSeconds} giây`
            : `${minutes} phút`;
    }

    const hours = Math.floor(minutes / 60);
    const remainMinutes = minutes % 60;

    if (remainMinutes > 0) {
        return `${hours} giờ ${remainMinutes} phút`;
    }

    return `${hours} giờ`;
}

/**
 * Format thời gian cuộc gọi (video/audio) theo kiểu tiếng Việt tự nhiên
 * @param {number|string|null|undefined} seconds - Số giây (có thể là string từ server)
 * @param {Object} [options] - Tùy chọn định dạng
 * @param {boolean} [options.short=false] - Nếu true: định dạng ngắn gọn (ví dụ: "2p30s", "1g15p")
 * @param {boolean} [options.zeroAsMissed=false] - Nếu true: 0 giây hiển thị "Cuộc gọi nhỡ" thay vì "0 giây"
 * @param {string} [options.missedText="Cuộc gọi nhỡ"] - Văn bản khi missed
 * @returns {string} Chuỗi thời gian đã format
 *
 * @example
 * formatDuration(45)                  → "45 giây"
 * formatDuration(125)                 → "2 phút 5 giây"
 * formatDuration(3600)                → "1 giờ"
 * formatDuration(0, {zeroAsMissed: true}) → "Cuộc gọi nhỡ"
 * formatDuration(3665, {short: true}) → "1g1p5s"
 */
export function formatDurations(seconds, options = {}) {
    const {
        short = false,
        zeroAsMissed = false,
        missedText = "Cuộc gọi nhỡ"
    } = options;

    // Xử lý input không hợp lệ
    if (seconds == null || isNaN(seconds)) {
        return zeroAsMissed ? missedText : "0 giây";
    }

    let totalSeconds = Math.max(0, Math.floor(Number(seconds)));

    if (totalSeconds === 0) {
        return zeroAsMissed ? missedText : "0 giây";
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (short) {
        // Định dạng ngắn gọn: 1g23p45s hoặc 5p30s hoặc 45s
        const parts = [];
        if (hours > 0) parts.push(`${hours}g`);
        if (minutes > 0 || hours > 0) parts.push(`${minutes}p`);
        if (secs > 0 || (minutes === 0 && hours === 0)) parts.push(`${secs}s`);
        return parts.join("") || "0s";
    }

    // Định dạng đầy đủ, tự nhiên tiếng Việt
    if (hours > 0) {
        if (minutes > 0) {
            return `${hours} giờ ${minutes} phút`;
        }
        return `${hours} giờ`;
    }

    if (minutes > 0) {
        if (secs > 0) {
            return `${minutes} phút ${secs} giây`;
        }
        return `${minutes} phút`;
    }

    return `${secs} giây`;
}
